import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch, RootState } from "../store";
import { toggleCreatorMode } from "../store/creatorModeSlice";
import {
  advanceCreatorModeSequence,
  EMPTY_CREATOR_MODE_SEQUENCE,
  isTypingTarget,
} from "../utils/creatorMode";

const NOTICE_MS = 2500;

// Listens for the Option+1→2→3 shortcut app-wide and shows a short notice when creator mode flips.
const CreatorModeListener: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const enabled = useSelector((state: RootState) => state.creatorMode.enabled);
  const sequenceRef = useRef(EMPTY_CREATOR_MODE_SEQUENCE);
  const [notice, setNotice] = useState<string | null>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isTypingTarget(event.target)) return;
      const result = advanceCreatorModeSequence(sequenceRef.current, event, Date.now());
      sequenceRef.current = result.sequence;
      if (result.toggle) {
        event.preventDefault();
        dispatch(toggleCreatorMode());
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dispatch]);

  // Notice on each change, not on mount
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setNotice(enabled ? "Creator mode on" : "Creator mode off");
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled]);

  if (!notice) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[1000] rounded-lg bg-eai-ink px-4 py-2 font-outfit text-sm text-white shadow-lg"
    >
      {notice}
    </div>
  );
};

export default CreatorModeListener;
